/**
 * WhatsApp Broadcast Configuration
 * 
 * Configuración centralizada para el canal de difusión de WhatsApp.
 * Se integra con BioTemplatePremium para mostrar un CTA sutil al final.
 * 
 * Gateway de pago según país:
 * - Argentina (AR): MercadoPago (ARS)
 * - Otros países: Stripe (USD)
 * 
 * Para habilitar en un país:
 * 1. Agregar el código ISO del país a enabledCountries
 * 2. Configurar precios en ARS (Argentina) y USD (otros)
 * 3. Configurar la URL de éxito (puede ser un form, página de bienvenida, etc.)
 */

export interface WhatsAppBroadcastConfig {
  /** Precio mensual en ARS para Argentina (e.g. 6999) */
  priceARS: number;
  /** Precio mensual en USD para otros países (e.g. 9.99) */
  priceUSD: number;
  /** Países donde se muestra (códigos ISO, e.g. ["AR", "MX", "US"]) */
  enabledCountries: string[];
  /** URL de éxito después del pago — leer de VITE_WHATSAPP_BROADCAST_SUCCESS_URL */
  successUrl: string;
  /** URL directa de MercadoPago para AR (VITE_WHATSAPP_BROADCAST_MP_URL) */
  mpCheckoutUrl: string;
  /** URL de imagen de fondo para la página (opcional) */
  backgroundImageUrl?: string;
  /** Admin API key para integraciones (opcional) */
  adminApiKey?: string;
}

/**
 * Configuración global de WhatsApp Broadcast
 * 
 * Gateway de pago según país:
 * - Argentina (AR): redirect directo a VITE_WHATSAPP_BROADCAST_MP_URL (sin email)
 * - Otros países: Stripe Embedded Checkout "Miembro Basic" (699 USD cents)
 *
 * Los textos (título, descripción, botones) están hardcodeados en el template
 * para simplificar — no requieren variables de entorno adicionales.
 */
export const WHATSAPP_BROADCAST_CONFIG: WhatsAppBroadcastConfig = {
  // Precio mensual en ARS (Argentina - MercadoPago)
  priceARS: parseFloat(import.meta.env.VITE_WHATSAPP_BROADCAST_PRICE_ARS || "6999"),
  
  // Precio mensual en USD (otros países - Stripe)
  priceUSD: parseFloat(import.meta.env.VITE_WHATSAPP_BROADCAST_PRICE_USD || "9.99"),
  
  // Países habilitados (agregar/quitar según necesidad)
  enabledCountries: (import.meta.env.VITE_WHATSAPP_BROADCAST_COUNTRIES || "AR,MX,ES")
    .split(",")
    .map(c => c.trim())
    .filter(Boolean),
  
  // URL de éxito (puede ser un form, página de bienvenida, etc.)
  successUrl: import.meta.env.VITE_WHATSAPP_BROADCAST_SUCCESS_URL ||
    `${import.meta.env.VITE_BRAND_FAN_URL || ""}/r/igcanal`,

  // URL directa de MercadoPago para suscripción AR (sin email, redirect directo)
  mpCheckoutUrl: import.meta.env.VITE_WHATSAPP_BROADCAST_MP_URL || "",

  // Imagen de fondo personalizada para la página
  backgroundImageUrl: import.meta.env.VITE_WHATSAPP_BROADCAST_BG_IMAGE || undefined,

  // Admin API key para el endpoint de suscripción de WhatsApp Broadcast.
  // Preferir VITE_WHATSAPP_BROADCAST_ADMIN_API_KEY; legacy: VITE_WHATSAPP_BROADCAST_VERCEL_KEY.
  adminApiKey:
    import.meta.env.VITE_WHATSAPP_BROADCAST_ADMIN_API_KEY ||
    import.meta.env.VITE_WHATSAPP_BROADCAST_VERCEL_KEY ||
    import.meta.env.VITE_ADMIN_API_KEY ||
    import.meta.env.VITE_VERCEL_API_KEY ||
    undefined,
};

/**
 * Verifica si WhatsApp Broadcast está habilitado para un país específico
 */
export const isWhatsAppBroadcastEnabledForCountry = (countryCode: string): boolean => {
  return WHATSAPP_BROADCAST_CONFIG.enabledCountries.includes(countryCode);
};

/**
 * Obtiene la configuración de WhatsApp Broadcast si está habilitada para el país
 */
export const getWhatsAppBroadcastConfig = (countryCode: string): WhatsAppBroadcastConfig | null => {
  if (!isWhatsAppBroadcastEnabledForCountry(countryCode)) {
    return null;
  }
  return WHATSAPP_BROADCAST_CONFIG;
};

/**
 * Obtiene la configuración de WhatsApp Broadcast para un país específico
 * Los textos se obtienen automáticamente de bioTemplatePremiumPlans.ts según el idioma
 */
export const getWhatsAppBroadcastConfigForCountry = (
  countryCode: string
): WhatsAppBroadcastConfig | null => {
  if (import.meta.env.DEV) {
    console.log("[whatsappBroadcastConfig] Checking country:", countryCode, {
      enabledCountries: WHATSAPP_BROADCAST_CONFIG.enabledCountries,
      isEnabled: WHATSAPP_BROADCAST_CONFIG.enabledCountries.includes(countryCode),
    });
  }

  return getWhatsAppBroadcastConfig(countryCode);
};

export default {
  WHATSAPP_BROADCAST_CONFIG,
  isWhatsAppBroadcastEnabledForCountry,
  getWhatsAppBroadcastConfig,
  getWhatsAppBroadcastConfigForCountry,
};
